import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { OPCIONES_GOOGLE_MAPS } from '../mapas/opcionesGoogleMaps';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { InputText } from 'primereact/inputtext';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { TIPOS, fechaHora, navegador, seguridadApi, severidad } from './seguridadApi';

/**
 * S-4 Tablero de seguridad de usuarios (ADR-002 S6): quien esta conectado, desde donde, alertas de
 * las ultimas 24 h y acciones sobre cada usuario. Se actualiza cada 60 s.
 */
const COLOR_ESTADO = { ACTIVA: '#1f8a4c', ENROLAR: '#b7791f', SUSPENDIDA: '#c2381f' };
const CENTRO_ECUADOR = { lat: -1.8, lng: -79.5 };

const Indicador = ({ titulo, valor, alerta }) => (
    <div className="col-6 md:col-3 xl:col">
        <div className="card mb-0 p-3" style={{ borderLeft: `4px solid ${alerta && valor > 0 ? '#c2381f' : '#2f5d8a'}` }}>
            <div className="text-600 text-sm">{titulo}</div>
            <div className="text-3xl font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{valor ?? '—'}</div>
        </div>
    </div>
);

/**
 * Mapa de sesiones (decision 20/10, opcion A + B): cada sesion se dibuja donde la ubico el navegador del usuario
 * (verde) o, si no la compartio, en la sede de su IP (azul). Las sesiones en el mismo punto se agrupan; el clic
 * muestra quienes son.
 */
const punto = (s) => {
    if (s.latitud != null && s.longitud != null) return { lat: Number(s.latitud), lng: Number(s.longitud), origen: 'navegador' };
    if (s.sede_latitud != null && s.sede_longitud != null) return { lat: Number(s.sede_latitud), lng: Number(s.sede_longitud), origen: 'sede' };
    return null;
};
const escapar = (v) => String(v == null ? '' : v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const MapaSesiones = ({ sesiones }) => {
    const { isLoaded } = useJsApiLoader(OPCIONES_GOOGLE_MAPS);
    const [mapa, setMapa] = useState(null);
    const grupos = useMemo(() => {
        const g = new Map();
        sesiones.forEach((s) => {
            const p = punto(s);
            if (!p) return;
            const k = `${p.origen}|${p.lat.toFixed(5)}|${p.lng.toFixed(5)}`;
            if (!g.has(k)) g.set(k, { ...p, sede: s.sede, sesiones: [] });
            g.get(k).sesiones.push(s);
        });
        return [...g.values()];
    }, [sesiones]);
    const cuenta = useMemo(() => ({
        navegador: sesiones.filter((s) => punto(s) && punto(s).origen === 'navegador').length,
        sede: sesiones.filter((s) => punto(s) && punto(s).origen === 'sede').length,
        sin: sesiones.filter((s) => !punto(s)).length
    }), [sesiones]);

    useEffect(() => {
        if (!isLoaded || !mapa || !window.google) return undefined;
        const g = window.google.maps;
        const info = new g.InfoWindow();
        const marcas = grupos.map((gr) => {
            const m = new g.Marker({
                map: mapa,
                position: { lat: gr.lat, lng: gr.lng },
                title: gr.origen === 'sede' ? `${gr.sede} (${gr.sesiones.length})` : gr.sesiones.map((s) => s.username).join(', '),
                label: gr.sesiones.length > 1 ? { text: String(gr.sesiones.length), color: '#fff', fontSize: '11px', fontWeight: '700' } : undefined,
                icon: { path: g.SymbolPath.CIRCLE, scale: gr.sesiones.length > 1 ? 13 : 8, fillColor: gr.origen === 'sede' ? '#2f5d8a' : '#1f8a4c',
                    fillOpacity: 0.92, strokeColor: '#fff', strokeWeight: 2 }
            });
            m.addListener('click', () => {
                const titulo = gr.origen === 'sede' ? `Sede: ${escapar(gr.sede)} <small>(por IP)</small>` : 'Ubicacion del navegador';
                const filas = gr.sesiones.map((s) => `<div style="margin-top:6px"><b>${escapar(s.username)}</b> · ${escapar(s.roles || '')}<br>`
                    + `<small>IP ${escapar(s.ip)} · ${escapar(navegador(s.user_agent))} · ultimo uso ${escapar(fechaHora(s.fecha_ultimo_uso))}</small></div>`).join('');
                info.setContent(`<div style="max-width:300px;max-height:240px;overflow:auto"><b>${titulo}</b>${filas}</div>`);
                info.open({ map: mapa, anchor: m });
            });
            return m;
        });
        if (grupos.length) {
            const b = new g.LatLngBounds();
            grupos.forEach((gr) => b.extend({ lat: gr.lat, lng: gr.lng }));
            mapa.fitBounds(b, 60);
            if (grupos.length === 1) mapa.setZoom(14);
        }
        return () => marcas.forEach((m) => m.setMap(null));
    }, [isLoaded, mapa, grupos]);

    if (!isLoaded) return <div className="p-4 text-600">Cargando mapa…</div>;
    return (
        <div>
            <GoogleMap mapContainerStyle={{ width: '100%', height: '22rem', borderRadius: 6 }} center={CENTRO_ECUADOR} zoom={6} onLoad={setMapa}
                options={{ streetViewControl: false, mapTypeControl: false }} />
            <small className="text-600">
                <span style={{ color: '#1f8a4c' }}>●</span> {cuenta.navegador} por ubicacion del navegador ·{' '}
                <span style={{ color: '#2f5d8a' }}>●</span> {cuenta.sede} por la sede de su IP · {cuenta.sin} sin ubicacion
                (su IP no tiene sede: asignela abajo en «Sedes por IP»).
            </small>
        </div>
    );
};

/** Elegir el punto de una sede haciendo clic en el mapa. */
const ElegirPunto = ({ valor, alElegir }) => {
    const { isLoaded } = useJsApiLoader(OPCIONES_GOOGLE_MAPS);
    const [mapa, setMapa] = useState(null);
    useEffect(() => {
        if (!isLoaded || !mapa || !window.google || !valor) return undefined;
        const m = new window.google.maps.Marker({ map: mapa, position: valor });
        return () => m.setMap(null);
    }, [isLoaded, mapa, valor]);
    if (!isLoaded) return <div className="p-3 text-600">Cargando mapa…</div>;
    return (
        <GoogleMap mapContainerStyle={{ width: '100%', height: '18rem', borderRadius: 6 }} center={valor || CENTRO_ECUADOR} zoom={valor ? 15 : 6}
            onLoad={setMapa} onClick={(e) => alElegir({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
            options={{ streetViewControl: false, mapTypeControl: true, draggableCursor: 'crosshair' }} />
    );
};

/** Sedes por IP: la tabla que ubica en el mapa a las sesiones que no compartieron su ubicacion. */
const SedesPorIp = ({ avisar, alCambiar }) => {
    const [datos, setDatos] = useState({ sedes: [], sinSede: [] });
    const [form, setForm] = useState(null);
    const [guardando, setGuardando] = useState(false);
    // el mapa del dialogo se crea recien cuando el dialogo termino de abrirse: si nace durante la animacion, Google
    // Maps calcula mal su tamano y queda gris (visto 20/10 en la prueba)
    const [dialogoListo, setDialogoListo] = useState(false);
    const cargar = useCallback(async () => {
        try {
            setDatos(await seguridadApi.ipSedes());
        } catch (e) {
            avisar('error', e.message);
        }
    }, [avisar]);
    useEffect(() => { cargar(); }, [cargar]);

    const guardar = async () => {
        setGuardando(true);
        try {
            await seguridadApi.guardarIpSede(form.ip, { sede: form.sede, latitud: form.punto && form.punto.lat, longitud: form.punto && form.punto.lng });
            avisar('success', `IP ${form.ip}: ${form.sede}`);
            setForm(null);
            await cargar();
            alCambiar();
        } catch (e) {
            avisar('error', e.message);
        } finally {
            setGuardando(false);
        }
    };
    const quitar = (ip) => confirmDialog({
        message: `¿Quitar la sede de la IP ${ip}? Sus sesiones dejan de verse en el mapa (salvo que compartan su ubicacion).`,
        header: 'Confirmar', icon: 'pi pi-exclamation-triangle', acceptLabel: 'Si', rejectLabel: 'No',
        accept: async () => {
            try {
                await seguridadApi.borrarIpSede(ip);
                await cargar();
                alCambiar();
            } catch (e) {
                avisar('error', e.message);
            }
        }
    });

    return (
        <div className="grid">
            <div className="col-12 lg:col-6">
                <h6 className="mt-0">IP sin sede (sesiones abiertas)</h6>
                <DataTable value={datos.sinSede} size="small" emptyMessage="Todas las IP con sesiones abiertas tienen sede" responsiveLayout="scroll">
                    <Column header="IP" field="ip" />
                    <Column header="Sesiones" field="sesiones" style={{ textAlign: 'right' }} />
                    <Column header="Usuarios" body={(r) => <small>{r.usuarios}</small>} />
                    <Column body={(r) => <Button label="Asignar sede" icon="pi pi-map-marker" className="p-button-sm p-button-outlined"
                        onClick={() => setForm({ ip: r.ip, sede: '', punto: null })} />} />
                </DataTable>
            </div>
            <div className="col-12 lg:col-6">
                <h6 className="mt-0">Sedes registradas</h6>
                <DataTable value={datos.sedes} size="small" emptyMessage="Sin sedes registradas" responsiveLayout="scroll">
                    <Column header="IP" field="ip" />
                    <Column header="Sede" field="sede" />
                    <Column header="Sesiones" field="sesiones" style={{ textAlign: 'right' }} />
                    <Column header="Cambio" body={(r) => <small>{r.usuario_modifica} · {fechaHora(r.fecha_modifica)}</small>} />
                    <Column body={(r) => (
                        <span className="flex gap-1">
                            <Button icon="pi pi-pencil" className="p-button-text" title="Editar" aria-label="Editar"
                                onClick={() => setForm({ ip: r.ip, sede: r.sede, punto: { lat: Number(r.latitud), lng: Number(r.longitud) } })} />
                            <Button icon="pi pi-trash" className="p-button-text p-button-danger" title="Quitar" aria-label="Quitar" onClick={() => quitar(r.ip)} />
                        </span>
                    )} />
                </DataTable>
            </div>
            <Dialog header={form ? `Sede de la IP ${form.ip}` : ''} visible={!!form} style={{ width: 'min(44rem, 96vw)' }} onShow={() => setDialogoListo(true)}
                onHide={() => { setForm(null); setDialogoListo(false); }}
                footer={form && (
                    <div>
                        <Button label="Cancelar" className="p-button-text" onClick={() => setForm(null)} />
                        <Button label="Guardar" icon="pi pi-save" loading={guardando} disabled={!form.sede.trim() || !form.punto} onClick={guardar} />
                    </div>
                )}>
                {form && (
                    <div className="p-fluid">
                        <label htmlFor="sede-nombre">Nombre de la sede</label>
                        <InputText id="sede-nombre" value={form.sede} maxLength={120} placeholder="Ej.: Oficina matriz, Patio R9"
                            onChange={(e) => setForm({ ...form, sede: e.target.value })} />
                        <div className="mt-3 mb-1">Haga clic en el mapa donde esta la sede{form.punto ? ` (${form.punto.lat.toFixed(5)}, ${form.punto.lng.toFixed(5)})` : ''}</div>
                        {dialogoListo ? <ElegirPunto valor={form.punto} alElegir={(p) => setForm((f) => ({ ...f, punto: p }))} />
                            : <div style={{ height: '18rem' }} className="p-3 text-600">Cargando mapa…</div>}
                    </div>
                )}
            </Dialog>
        </div>
    );
};

const SeguridadUsuarios = () => {
    const toast = useRef(null);
    const [tablero, setTablero] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [filtro, setFiltro] = useState('');
    const [detalle, setDetalle] = useState(null);
    const [cargando, setCargando] = useState(false);

    // estable: SedesPorIp la usa en un useCallback y, si cambiara en cada render, recargaria sin parar
    const avisar = useCallback((severity, detail) => toast.current && toast.current.show({ severity, summary: severity === 'error' ? 'Error' : 'Listo', detail, life: 4000 }), []);

    const cargar = useCallback(async () => {
        setCargando(true);
        try {
            const [t, u] = await Promise.all([seguridadApi.tablero(), seguridadApi.usuarios()]);
            setTablero(t);
            setUsuarios(u);
        } catch (e) {
            avisar('error', e.message);
        } finally {
            setCargando(false);
        }
    }, []);

    useEffect(() => {
        cargar();
        const t = setInterval(cargar, 60000);
        return () => clearInterval(t);
    }, [cargar]);

    const abrirDetalle = async (u) => {
        try {
            setDetalle({ usuario: u, datos: await seguridadApi.usuario(u.usuario_id) });
        } catch (e) {
            avisar('error', e.message);
        }
    };

    const accion = (mensaje, fn, hecho) => confirmDialog({
        message: mensaje,
        header: 'Confirmar',
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Si',
        rejectLabel: 'No',
        accept: async () => {
            try {
                await fn();
                avisar('success', hecho);
                await cargar();
                if (detalle) abrirDetalle(detalle.usuario);
            } catch (e) {
                avisar('error', e.message);
            }
        }
    });

    const ind = (tablero && tablero.indicadores) || {};
    const sesiones = (tablero && tablero.sesiones) || [];
    const alertas = (tablero && tablero.alertas) || [];
    const usuariosFiltrados = useMemo(() => {
        const f = filtro.trim().toLowerCase();
        return f ? usuarios.filter((u) => `${u.username} ${u.nombre || ''} ${u.roles || ''}`.toLowerCase().includes(f)) : usuarios;
    }, [usuarios, filtro]);

    const estadoUsuario = (u) => {
        if (u.bloqueado_hasta && new Date(u.bloqueado_hasta) > new Date()) return <Tag severity="danger" value="Bloqueado" />;
        if (u.intentos_fallidos > 0) return <Tag severity="warning" value={`${u.intentos_fallidos} fallos`} />;
        return <Tag severity="success" value="Normal" />;
    };

    return (
        <div className="grid">
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="col-12 flex align-items-center justify-content-between flex-wrap gap-2">
                <h4 className="m-0">Seguridad de usuarios</h4>
                <Button label="Actualizar" icon="pi pi-refresh" className="p-button-outlined" onClick={cargar} loading={cargando} />
            </div>
            <div className="col-12">
                <div className="grid">
                    <Indicador titulo="Conectados ahora (15 min)" valor={ind.conectados} />
                    <Indicador titulo="Sesiones abiertas" valor={ind.sesiones_abiertas} />
                    <Indicador titulo="Ingresos hoy" valor={ind.ingresos_hoy} />
                    <Indicador titulo="Fallos hoy" valor={ind.fallos_hoy} alerta />
                    <Indicador titulo="Bloqueados" valor={ind.bloqueados} alerta />
                    <Indicador titulo="Fuera de geocerca" valor={ind.fuera_geocerca} alerta />
                    <Indicador titulo="Con segundo factor" valor={ind.con_segundo_factor} />
                </div>
            </div>
            <div className="col-12 xl:col-7">
                <div className="card h-full">
                    <h5>Sesiones abiertas</h5>
                    <DataTable value={sesiones} size="small" scrollable scrollHeight="22rem" emptyMessage="Nadie conectado" responsiveLayout="scroll">
                        <Column header="Usuario" body={(s) => <div><b>{s.username}</b><br /><small className="text-600">{s.roles}</small></div>} />
                        <Column header="Estado" body={(s) => <Tag value={s.estado} style={{ background: COLOR_ESTADO[s.estado] }} />} />
                        <Column header="Desde" body={(s) => <small>{s.ip}<br />{navegador(s.user_agent)} · {s.cliente === 'WEB' ? 'Web' : 'App/otro'}</small>} />
                        <Column header="Ultimo uso" body={(s) => <small>{fechaHora(s.fecha_ultimo_uso)}</small>} />
                        <Column body={(s) => (
                            <Button icon="pi pi-sign-out" className="p-button-text p-button-danger" title="Cerrar esta sesion" aria-label="Cerrar esta sesion"
                                onClick={() => accion(`¿Cerrar la sesion de ${s.username}? Tendra que volver a ingresar.`, () => seguridadApi.cerrarSesion(s.sesion_id), 'Sesion cerrada')} />
                        )} />
                    </DataTable>
                </div>
            </div>
            <div className="col-12 xl:col-5">
                <div className="card h-full">
                    <h5>Mapa de sesiones</h5>
                    <MapaSesiones sesiones={sesiones} />
                </div>
            </div>
            <div className="col-12">
                <div className="card">
                    <h5>Sedes por IP</h5>
                    <p className="text-600 mt-0">Las sesiones que no comparten su ubicacion se ubican en el mapa en la sede de su IP.</p>
                    <SedesPorIp avisar={avisar} alCambiar={cargar} />
                </div>
            </div>
            <div className="col-12">
                <div className="card">
                    <h5>Alertas de las ultimas 24 horas</h5>
                    <DataTable value={alertas} size="small" paginator rows={8} emptyMessage="Sin alertas" responsiveLayout="scroll">
                        <Column header="Fecha" body={(a) => fechaHora(a.fecha)} />
                        <Column header="Usuario" field="username" />
                        <Column header="Evento" body={(a) => <Tag severity={severidad(a.resultado)} value={TIPOS[a.tipo] || a.tipo} />} />
                        <Column header="IP" field="ip" />
                        <Column header="Detalle" field="detalle" />
                    </DataTable>
                </div>
            </div>
            <div className="col-12">
                <div className="card">
                    <div className="flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                        <h5 className="m-0">Usuarios activos</h5>
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText id="filtro-usuarios" value={filtro} onChange={(e) => setFiltro(e.target.value)} placeholder="Usuario, nombre o rol" />
                        </span>
                    </div>
                    <DataTable value={usuariosFiltrados} size="small" paginator rows={15} sortField="ultimo_ingreso" sortOrder={-1}
                        onRowClick={(e) => abrirDetalle(e.data)} rowHover style={{ cursor: 'pointer' }} responsiveLayout="scroll">
                        <Column header="Usuario" field="username" sortable />
                        <Column header="Nombre" field="nombre" sortable />
                        <Column header="Roles" field="roles" />
                        <Column header="Estado" body={estadoUsuario} />
                        <Column header="Segundo factor" body={(u) => (u.segundo_factor_activo ? <Tag severity="success" value="Activo" /> : <span className="text-500">—</span>)} />
                        <Column header="Ultimo ingreso" field="ultimo_ingreso" sortable body={(u) => fechaHora(u.ultimo_ingreso)} />
                        <Column header="Ingresos 30 dias" field="ingresos_30_dias" sortable style={{ textAlign: 'right' }} />
                    </DataTable>
                </div>
            </div>

            <Dialog header={detalle ? `Seguridad de ${detalle.usuario.username}` : ''} visible={!!detalle} style={{ width: 'min(60rem, 96vw)' }} onHide={() => setDetalle(null)}>
                {detalle && (
                    <div className="flex flex-column gap-3">
                        <div className="flex flex-wrap gap-2">
                            <Button label="Cerrar sus sesiones" icon="pi pi-sign-out" className="p-button-outlined"
                                onClick={() => accion('¿Cerrar todas las sesiones de este usuario?', () => seguridadApi.cerrarSesiones(detalle.usuario.usuario_id), 'Sesiones cerradas')} />
                            {detalle.usuario.bloqueado_hasta && new Date(detalle.usuario.bloqueado_hasta) > new Date() ? (
                                <Button label="Desbloquear" icon="pi pi-lock-open" className="p-button-success p-button-outlined"
                                    onClick={() => accion('¿Desbloquear el usuario?', () => seguridadApi.desbloquear(detalle.usuario.usuario_id), 'Usuario desbloqueado')} />
                            ) : (
                                <Button label="Bloquear" icon="pi pi-lock" className="p-button-danger p-button-outlined"
                                    onClick={() => accion('¿Bloquear el usuario? Se cierran sus sesiones y no podra ingresar hasta que lo desbloquee.',
                                        () => seguridadApi.bloquear(detalle.usuario.usuario_id), 'Usuario bloqueado')} />
                            )}
                            {detalle.usuario.segundo_factor_activo && (
                                <Button label="Reiniciar segundo factor" icon="pi pi-mobile" className="p-button-warning p-button-outlined"
                                    onClick={() => accion('¿Reiniciar el segundo factor? El usuario tendra que activarlo de nuevo (por ejemplo, si cambio de celular).',
                                        () => seguridadApi.reiniciarSegundoFactor(detalle.usuario.usuario_id), 'Segundo factor reiniciado')} />
                            )}
                        </div>
                        <div className="text-700">
                            Politica efectiva: segundo factor <b>{detalle.datos.politicaEfectiva.segundoFactor}</b>, geocerca{' '}
                            <b>{detalle.datos.politicaEfectiva.geocerca ? 'si' : 'no'}</b>
                            {detalle.datos.politicaEfectiva.ips && detalle.datos.politicaEfectiva.ips.length > 0 && <>, IP {detalle.datos.politicaEfectiva.ips.join(', ')}</>}
                        </div>
                        <TabView>
                            <TabPanel header="Historial">
                                <DataTable value={detalle.datos.historial} size="small" paginator rows={10} emptyMessage="Sin registros" responsiveLayout="scroll">
                                    <Column header="Fecha" body={(a) => fechaHora(a.fecha)} />
                                    <Column header="Evento" body={(a) => <Tag severity={severidad(a.resultado)} value={TIPOS[a.tipo] || a.tipo} />} />
                                    <Column header="IP" field="ip" />
                                    <Column header="Detalle" field="detalle" />
                                </DataTable>
                            </TabPanel>
                            <TabPanel header="Desde donde ingresa">
                                <DataTable value={detalle.datos.porIp} size="small" emptyMessage="Sin registros" responsiveLayout="scroll">
                                    <Column header="IP" field="ip" />
                                    <Column header="Ingresos" field="ingresos" style={{ textAlign: 'right' }} />
                                    <Column header="Fallos" field="fallos" style={{ textAlign: 'right' }} />
                                    <Column header="Ultima vez" body={(a) => fechaHora(a.ultima)} />
                                </DataTable>
                            </TabPanel>
                        </TabView>
                    </div>
                )}
            </Dialog>
        </div>
    );
};

export default SeguridadUsuarios;
